import { useState } from 'react';
import * as S from './Input.styles';
import Label from '../texts/label/Label';
import Icon from '../icon/Icon';
import Span from '../texts/span/Span';

const spanVariantByStatus = {
  normal: 'neutral',
  error: 'danger',
};
export type InputProps = {
  id?: string;
  name?: string;
  value?: string;
  label?: string;
  type?: 'text' | 'number' | 'password' | 'check' | 'email' | 'file';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  inputStatus?: 'normal' | 'error';
  readOnly?: boolean;
  placeholder?: string;
  onChange?: (e) => any;
  onBlur?: (e) => any;
  onFocus?: (e) => any;
  onKeydown?: (e) => void;
  onKeyup?: (e) => void;
  onPaste?: (e) => void;
  message?: string;
  disabled?: boolean;
  maxLength?: number;
  autocomplete?: string;
  onlyNumbers?: boolean;
  showPasswordButton?: boolean;
  onClick?: () => any;
  inputRef?: any;
  maxLengthHelpText?: boolean;
  pattern?: string;
  icons?: {
    iconLeft?: any;
    iconRight?: any;
  };
  accept?: string;
  variant?: 'transparent';
  modifier?: 'weak';
  ref?: any;
};

const validateOnlyNumbers = (onlyNumbers, e, innerValue, setInnerValue) => {
  if (onlyNumbers) {
    const numberValue = e.target.validity.valid ? e.target.value : innerValue;
    setInnerValue(numberValue);
    e.target.value = numberValue;
  }
};

const Input = (props: InputProps) => {
  const [hasFocus, setFocus] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);
  const [innerValue, setInnerValue] = useState(props.value);
  const [inputType, setInputType] = useState(
    props.onlyNumbers ? 'text' : props.type
  );

  const handleShowPasswordClick = () => {
    setInputType(!showPassword ? 'text' : 'password');
    setShowPassword(!showPassword);
  };

  const proxyOnChange = (e) => {
    validateOnlyNumbers(props.onlyNumbers, e, innerValue, setInnerValue);
    setCharacterCount(e.target.value.length);
    props.onChange && props.onChange(e);
  };

  const proxyOnBlur = (e) => {
    validateOnlyNumbers(props.onlyNumbers, e, innerValue, setInnerValue);
    setFocus(false);
    props.onBlur && props.onBlur(e);
  };

  const proxyOnFocus = (e) => {
    validateOnlyNumbers(props.onlyNumbers, e, innerValue, setInnerValue);
    setFocus(true);
    props.onFocus && props.onFocus(e);
  };

  const renderIcon = (icon, align) => {
    const isRight = align === 'right';
    const callback =
      isRight && props.icons?.onIconClick
        ? props.icons.onIconClick
        : icon.callback;

    const name = typeof icon === 'string' ? icon : icon.name || icon;

    return (
      <>
        {props.disabled ? (
          <Icon name={name} variant='neutral' cursor={'not-allowed'} />
        ) : (
          <Icon
            name={name}
            variant='neutral'
            cursor={callback ? 'pointer' : 'default'}
            modifier={align == 'left' ? 'weak' : 'strong'}
            onClick={() => callback && callback()}
          />
        )}
      </>
    );
  };

  const renderShowPasswordButton = () => (
    <>
      {props.type === 'password' && (
        <>
          {showPassword ? (
            <Icon
              onClick={handleShowPasswordClick}
              name='view_outline'
              variant='neutral'
              cursor={props.disabled ? 'not-allowed' : 'pointer'}
              modifier={props.disabled ? 'regular' : 'strong'}
            />
          ) : (
            <Icon
              onClick={handleShowPasswordClick}
              name='hide_outline'
              variant='neutral'
              cursor={props.disabled ? 'not-allowed' : 'pointer'}
              modifier={props.disabled ? 'regular' : 'strong'}
            />
          )}
        </>
      )}
    </>
  );

  const renderMaxLengthHelpText = () => (
    <S.SiblingInputTag>
      <Span
        font='primary'
        type='2'
        variant='neutral'
        weight='regular'
        modifier='regular'
      >
        {`${characterCount}/${props.maxLength}`}
      </Span>
    </S.SiblingInputTag>
  );

  return (
    <S.Input>
      {props.label && (
        <S.LabelWrapper>
          <Label id={props.id} type={'4'} modifier={props.modifier === 'weak' ? 'weak' : 'strong'}>
            {props.label}
          </Label>
        </S.LabelWrapper>
      )}
      <S.BaseInput
        hasFocus={hasFocus}
        size={props.size}
        status={props.inputStatus}
        disabled={props.disabled}
        readOnly={props.readOnly}
        onlyNumbers={props.onlyNumbers}
        variant={props.variant}
        modifier={props.modifier}
        ref={props.ref}
      >
        {props.icons?.iconLeft && renderIcon(props.icons.iconLeft, 'left')}
        <S.InputTag
          id={props.id}
          type={inputType}
          placeholder={props.placeholder}
          onClick={props.onClick}
          onChange={proxyOnChange}
          onBlur={proxyOnBlur}
          onFocus={proxyOnFocus}
          onKeyDown={props.onKeydown}
          onKeyUp={props.onKeyup}
          onPaste={props.onPaste}
          pattern={props.onlyNumbers ? '^[0-9]+$' : props.pattern}
          {...(inputType !== 'file' && { value: props.value })}
          disabled={props.disabled}
          readOnly={props.readOnly}
          maxLength={props.maxLength}
          ref={props.inputRef}
          autoComplete={props.autocomplete}
          accept={props.accept}
        />
        {props.icons?.iconRight && renderIcon(props.icons.iconRight, 'right')}
        {props.showPasswordButton && renderShowPasswordButton()}
        {props.maxLengthHelpText && renderMaxLengthHelpText()}
      </S.BaseInput>
      {props.message && (
        <S.HelpText status={props.inputStatus}>
          <Span
            id={props.id}
            font='primary'
            modifier='regular'
            type='4'
            weight='regular'
            variant={spanVariantByStatus[props.inputStatus]}
          >
            {props.message}
          </Span>
        </S.HelpText>
      )}
    </S.Input>
  );
};

const RangeInput = ({ from, to, ...props }) => (
  <Input value={`${from}${to}`} {...props} />
);

const NumberInput = ({ ...props }) => <Input {...props} onlyNumbers />;

export { RangeInput, NumberInput };

export default Input;
